import { useMemo, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { token } from '../theme/tokens.js';

export interface DataTableColumn<TRow> {
  /** Stable key. Also the sort key. */
  id: string;
  /** Header cell content. A string here is copy the consumer owns. */
  header: ReactNode;
  /** Cell renderer. */
  cell: (row: TRow) => ReactNode;
  /** Enable click-to-sort on this column. Requires `sortValue`. */
  sortable?: boolean;
  /** Comparable value for sorting. */
  sortValue?: (row: TRow) => SortValue;
  /**
   * Right-align numeric columns; centre a column whose cell is a single glyph.
   *
   * **It governs the header as well as the cells**, which is the reason it is a
   * declaration here rather than a class on the cell: a centred column under a
   * left-aligned label reads as an accident, and a caller styling one `<td>`
   * has no way to reach the `<th>` above it.
   */
  align?: 'start' | 'center' | 'end';
}

export type SortDirection = 'asc' | 'desc';

export interface DataTableProps<TRow> {
  columns: ReadonlyArray<DataTableColumn<TRow>>;
  rows: readonly TRow[];
  /** Stable identity per row. Index is a bug once rows reorder. */
  rowKey: (row: TRow) => string;
  /** Accessible caption for the table. */
  caption: string;
  /**
   * Where the table rests before anybody clicks a header.
   *
   * A **starting position, not a mode**: the first click on the same column
   * flips it, exactly as it would have flipped a sort the reader had set
   * themselves, and nothing returns the table here afterwards.
   *
   * Omitting it leaves the rows in the order the caller supplied them, which
   * is right for a server-paged list — a client-side sort would order one page
   * and present it as the whole. `id` must name a column carrying `sortValue`;
   * one that does not is ignored, the same as an unsortable column click.
   */
  defaultSort?: { id: string; direction: SortDirection };
  /** Shown when `rows` is empty. */
  emptyState?: ReactNode;
  onRowClick?: (row: TRow) => void;
  className?: string;
}

/** The value space `DataTableColumn.sortValue` is allowed to return. */
export type SortValue = string | number | boolean | null | undefined;

/**
 * The comparator this table sorts by — **exported so that a card grid rendered
 * beside a table can rest in the same order.**
 *
 * Both views of one list have to agree, and the way they stop agreeing is
 * somebody writing a second comparator with a bare `localeCompare`: no
 * `numeric`, so `file10` sorts before `file2`, and no `sensitivity`, so a
 * capitalised name jumps the alphabet. One definition, two callers.
 *
 * **A missing value sorts first ascending**, which is worth knowing before you
 * add a nullable sort column: a space nobody has touched leads a Last activity
 * sort rather than trailing it.
 */
export function compareSortValues(a: SortValue, b: SortValue): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * One column's alignment, as a class, for the header and the cells alike.
 *
 * Written out rather than interpolated (`text-${…}`) because Tailwind scans
 * source text for whole class names and would emit none of these.
 */
function alignClass(align: DataTableColumn<unknown>['align']): string {
  if (align === 'end') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
}

/**
 * Sortable data table.
 *
 * Sorting is client-side and stable. For server-paginated data, sort upstream
 * and leave `sortable` off — a half-sorted page is worse than an unsorted one.
 * `defaultSort` is subject to the same rule, and for the same reason.
 */
export function DataTable<TRow>({
  columns,
  rows,
  rowKey,
  caption,
  defaultSort,
  emptyState,
  onRowClick,
  className,
}: DataTableProps<TRow>) {
  // Seeded once, deliberately: a `defaultSort` that changed identity between
  // renders would otherwise be free to yank the table back from wherever the
  // reader had just sorted it.
  const [sort, setSort] = useState<{ id: string; direction: SortDirection } | null>(
    defaultSort ?? null,
  );

  const sortedRows = useMemo(() => {
    if (sort === null) return rows;
    const column = columns.find((c) => c.id === sort.id);
    if (!column?.sortValue) return rows;
    const { sortValue } = column;
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => compareSortValues(sortValue(a), sortValue(b)) * factor);
  }, [rows, columns, sort]);

  const toggleSort = (id: string) => {
    setSort((current) =>
      current?.id === id
        ? { id, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { id, direction: 'asc' },
    );
  };

  return (
    // No card fill and no outer border. The table *is* the content of the
    // region it sits in, and wrapping it in a filled slab makes a row harder to
    // follow across than the hairlines between rows already make it easy.
    <div className={cn('w-full overflow-x-auto', className)}>
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => {
              const isSorted = sort?.id === column.id;
              const canSort = column.sortable === true && typeof column.sortValue === 'function';
              return (
                <th
                  key={column.id}
                  scope="col"
                  aria-sort={
                    isSorted ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'
                  }
                  className={cn('border-b px-3 py-2.5 font-medium', alignClass(column.align))}
                  // `bp-text-secondary`, not `bp-text-muted`: the muted rung is
                  // 4.1:1 on the app background, under AA for type this small.
                  style={{
                    borderColor: token('bp-border-strong'),
                    color: token('bp-text-secondary'),
                  }}
                >
                  {canSort ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(column.id)}
                      className="inline-flex items-center gap-1 rounded hover:underline focus-visible:outline-2"
                      style={{ outlineColor: token('bp-blue-rail') }}
                    >
                      {column.header}
                      <span aria-hidden="true">
                        {isSorted ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-3 py-8 text-center"
                style={{ color: token('bp-text-secondary') }}
              >
                {emptyState}
              </td>
            </tr>
          ) : (
            sortedRows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn('border-b last:border-0', onRowClick && 'cursor-pointer')}
                style={{ borderColor: token('bp-border') }}
              >
                {columns.map((column) => (
                  <td key={column.id} className={cn('px-3 py-3', alignClass(column.align))}>
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
